import React from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';

/**
 * 「有未保存的改动」三选一弹窗：**保存 / 不保存 / 取消**。
 *
 * 为什么不复用 `GlobalConfirmModal`：它只有两个按钮，而且主按钮被染成危险色
 * （"确定删除"）。这里有三个语义完全不同的出口，最重要的是每个按钮都要**说清后果**，
 * 所以按钮文案由调用方给全：
 *   · 保存      → 先落库再做那件事；
 *   · 不保存    → 丢掉改动直接做（丢掉是不可逆的，所以它是次按钮而不是强调按钮）；
 *   · 取消      → 留在原地（默认动作：Esc、点遮罩都是它）。
 */
export function UnsavedChangesModal({
  show,
  title = '有未保存的改动',
  message,
  saveText = '保存',
  discardText = '不保存',
  cancelText = '取消',
  saving = false,
  onSave,
  onDiscard,
  onCancel,
}: {
  show: boolean;
  title?: string;
  message: string;
  saveText?: string;
  discardText?: string;
  cancelText?: string;
  /** 保存中：按钮禁用，避免连点造成两次写入 */
  saving?: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  const dismiss = useModalDismiss(onCancel, { overlay: !saving, esc: !saving });
  if (!show) return null;

  const baseButton: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    whiteSpace: 'nowrap',
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'var(--modal-mask)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 2100,
      }}
      {...dismiss}
      data-testid="unsaved-changes-modal"
    >
      <div style={{
        backgroundColor: 'var(--card-bg)', padding: '24px', borderRadius: 12, width: 340,
        maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box',
        boxShadow: '0 10px 25px var(--shadow-color)', color: 'var(--text-color)',
      }}>
        <h3 style={{ marginBottom: 12, fontSize: 18, fontWeight: 600 }}>{title}</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{ ...baseButton, border: '1px solid var(--border-color)', backgroundColor: 'transparent', color: 'var(--text-secondary)' }}
          >
            {cancelText}
          </button>
          {/* 「不保存」放中间：它左边是"留在原地"、右边是"保存后再走"，
              两种"继续"的语义各自贴着自己那一侧，不会挨着误点。 */}
          <button
            type="button"
            onClick={onDiscard}
            disabled={saving}
            data-testid="unsaved-discard"
            style={{ ...baseButton, border: '1px solid var(--border-color)', backgroundColor: 'transparent', color: 'var(--danger-color)' }}
          >
            {discardText}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            data-testid="unsaved-save"
            style={{
              ...baseButton, border: 'none', backgroundColor: 'var(--primary-color)',
              color: 'white', fontWeight: 500, opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? '保存中…' : saveText}
          </button>
        </div>
      </div>
    </div>
  );
}
